import './src/styles/global.css';
import './src/localization/i18n';
import React, { useEffect } from 'react';
import { SnackbarProvider } from 'notistack';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { isWeb } from './src/utilities/platform';
import { BuyerStoreProvider } from './src/stores/buyer/buyer-store-context';
import { NativeBridgeBootstrap } from './src/components/buyer/NativeBridgeBootstrap';
import BridgeDevPanel from './src/components/dev/BridgeDevPanel';
import { syncTokenFromNative } from './src/bridge';

// Dev-only: restore a simulated `__VMALL_NATIVE__` from sessionStorage so that
// a console-pasted simulation survives `location.reload()`. Browser-mode is
// untouched in prod and when no stash exists.
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  try {
    const stash = window.sessionStorage?.getItem('__VMALL_NATIVE_DEV__');
    if (stash && !window.__VMALL_NATIVE__) {
      window.__VMALL_NATIVE__ = JSON.parse(stash);
    }
  } catch (err) {
    // ignore — bad JSON or storage disabled
  }
}

// Mirror the native-injected token into localStorage BEFORE wrapRootElement
// runs and instantiates BuyerStoreProvider — AuthStore's constructor reads
// localStorage synchronously, so deferring this to NativeBridgeBootstrap's
// useEffect leaves the store hydrated as guest until a manual reload.
if (typeof window !== 'undefined') {
  syncTokenFromNative();
}

const green = {
  50: '#f0faf4',
  100: '#dcf3e3',
  200: '#b8e6c8',
  300: '#8dd3a6',
  400: '#5cba84',
  500: '#34a36a',
  600: '#248856',
  700: '#1d6d47',
  800: '#19553a',
  900: '#143f2c',
};

const neutral = {
  0: '#ffffff',
  50: '#f8faf9',
  100: '#f1f4f3',
  200: '#e6ebe9',
  300: '#d2d9d6',
  400: '#9ba6a1',
  500: '#6b7672',
  600: '#4a544f',
  700: '#2f3633',
  800: '#1c211f',
};

const sh = (level) => {
  switch (level) {
    case 1:
      return '0 1px 2px rgba(20,63,44,.04), 0 1px 3px rgba(20,63,44,.03)';
    case 2:
      return '0 2px 4px rgba(20,63,44,.04), 0 6px 14px rgba(20,63,44,.06)';
    case 3:
      return '0 6px 12px rgba(20,63,44,.06), 0 18px 32px rgba(20,63,44,.08)';
    default:
      return 'none';
  }
};

// Replace MUI's default 25-step shadow ladder with green-tinted versions.
const shadows = ['none'];
for (let i = 1; i <= 24; i += 1) {
  if (i <= 4) shadows.push(sh(1));
  else if (i <= 12) shadows.push(sh(2));
  else shadows.push(sh(3));
}

const theme = createTheme({
  palette: {
    primary: {
      light: green[400],
      main: green[500],
      dark: green[600],
      contrastText: '#ffffff',
    },
    secondary: { main: neutral[600] },
    success: { main: green[600], light: green[100], dark: green[700], contrastText: '#ffffff' },
    warning: { main: '#b45309', light: '#fef3c7', contrastText: '#ffffff' },
    error: { main: '#b91c1c', light: '#fee2e2', contrastText: '#ffffff' },
    info: { main: '#2563eb', light: '#dbeafe', contrastText: '#ffffff' },
    background: { default: neutral[50], paper: neutral[0] },
    text: {
      primary: neutral[800],
      secondary: neutral[500],
      disabled: neutral[400],
    },
    divider: neutral[200],
    grey: {
      50: neutral[50],
      100: neutral[100],
      200: neutral[200],
      300: neutral[300],
      400: neutral[400],
      500: neutral[500],
      600: neutral[600],
      700: neutral[700],
      800: neutral[800],
      900: '#0f1311',
    },
  },
  shape: { borderRadius: 12 },
  shadows,
  typography: {
    fontFamily:
      '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
    h1: { fontWeight: 800, fontSize: '2.25rem', lineHeight: 1.16, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.21, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.4, letterSpacing: '-0.01em' },
    h4: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.375 },
    h5: { fontWeight: 600, fontSize: '0.9375rem' },
    h6: { fontWeight: 600, fontSize: '0.875rem' },
    body1: { fontSize: '0.875rem', lineHeight: 1.57 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.54 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    overline: {
      fontWeight: 700,
      fontSize: '0.6875rem',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      lineHeight: 1.45,
    },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFeatureSettings: '"cv02","cv03","cv04","cv11"',
          WebkitFontSmoothing: 'antialiased',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: false },
      styleOverrides: {
        root: { borderRadius: 10, paddingInline: 16, paddingBlock: 8, fontSize: 13 },
        sizeSmall: { borderRadius: 10, paddingInline: 12, paddingBlock: 6, fontSize: 12 },
        sizeLarge: { borderRadius: 12, paddingInline: 22, paddingBlock: 12, fontSize: 14 },
        containedPrimary: {
          background: `linear-gradient(180deg, ${green[500]}, ${green[600]})`,
          boxShadow: '0 8px 24px rgba(52,163,106,.18)',
          '&:hover': {
            background: `linear-gradient(180deg, ${green[600]}, ${green[700]})`,
            boxShadow: '0 8px 24px rgba(52,163,106,.24)',
          },
        },
        outlinedPrimary: {
          borderColor: green[300],
          color: green[700],
          '&:hover': { backgroundColor: green[50], borderColor: green[400] },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiTextField: {
      defaultProps: { size: 'small', fullWidth: true, variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: neutral[0],
          '& .MuiOutlinedInput-notchedOutline': { borderColor: neutral[200] },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: neutral[300] },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: green[500],
            borderWidth: 1,
            boxShadow: '0 0 0 3px rgba(52,163,106,.15)',
          },
        },
      },
    },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: 13, color: neutral[500] },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 16 },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${neutral[200]}`,
          boxShadow: sh(1),
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 20 },
        // Push every Dialog below the 64px sticky topbar on mobile so it
        // doesn't cover the menu / cart / avatar controls. Desktop is
        // unaffected — alignment falls back to MUI default (center).
        container: {
          '@media (max-width: 600px)': {
            alignItems: 'flex-start',
            paddingTop: 96,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { borderRadius: 0 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: neutral[500],
          backgroundColor: neutral[50],
          borderBottom: `1px solid ${neutral[200]}`,
        },
        root: { borderBottom: `1px solid ${neutral[200]}`, color: neutral[700] },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: green[50] },
          '&:last-child td': { borderBottom: 0 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontWeight: 600, fontSize: 11 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral[800],
          fontSize: 12,
          borderRadius: 8,
          padding: '6px 10px',
        },
        arrow: { color: neutral[800] },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: neutral[200] } },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, height: 6, backgroundColor: neutral[100] },
        bar: { borderRadius: 999 },
      },
    },
  },
});

const LocalizationProvider = ({ children }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (isWeb && localStorage.LANG) i18n.changeLanguage(localStorage.LANG);
    else {
      i18n.changeLanguage('en-AU');
      if (isWeb) localStorage.LANG = 'en-AU';
    }
  }, []);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
};

export const wrapRootElement = ({ element }) => (
  <SnackbarProvider
    maxSnack={3}
    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    autoHideDuration={3500}
  >
    <ThemeProvider theme={theme}>
      <BuyerStoreProvider>
        <NativeBridgeBootstrap />
        <LocalizationProvider>{element}</LocalizationProvider>
        {/* <BridgeDevPanel /> */}
      </BuyerStoreProvider>
    </ThemeProvider>
  </SnackbarProvider>
);
